import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import EventImage from "@/components/EventImage";
import EventMap from "@/components/EventMap";
import NearYou from "@/components/NearYou";
import FeaturedPros from "@/components/FeaturedPros";
import ClanHighlight from "@/components/ClanHighlight";
import ClanSpotlight from "@/components/ClanSpotlight";
import MoonPhase from "@/components/MoonPhase";
import SabbatBanner from "@/components/SabbatBanner";
import { useLang } from "@/context/LanguageContext";
import { RITUAL_PHRASES } from "@/i18n/translations";
import { CATEGORY_META } from "@/lib/categories";
import { formatPrice } from "@/lib/price";
import { weekendRange, isThisWeekend } from "@/lib/geo";
import { ArrowRight, Flame, Music, Sparkles, CalendarDays, Store, Tent } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1753006989736-b2d03b924527?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const MARKET_CATS = ["market", "medieval_market", "viking_market"];
const FESTIVAL_CATS = ["pagan_festival", "reenactment"];
const RITUAL_CATS = ["pagan_event"];

const RotatingRitual = () => {
  const { lang } = useLang();
  const phrases = RITUAL_PHRASES[lang] || RITUAL_PHRASES.en;
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % phrases.length), 3800);
    return () => clearInterval(id);
  }, [phrases.length]);
  return (
    <div className="h-8 mb-6 overflow-hidden" data-testid="ritual-rotator">
      <AnimatePresence mode="wait">
        <motion.p
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.55, ease: "easeInOut" }}
          className="font-serif italic text-amber-300/85 text-base sm:text-lg tracking-wide"
        >
          {phrases[i]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const SABBAT_DATES = [["imbolc", 1, 1], ["ostara", 2, 20], ["beltane", 4, 1], ["litha", 5, 21], ["lughnasadh", 7, 1], ["mabon", 8, 21], ["samhain", 9, 31], ["yule", 11, 21]];
const currentSabbat = () => {
  const now = new Date();
  const md = now.getMonth() * 100 + now.getDate();
  let cur = "yule";
  for (const [key, m, d] of SABBAT_DATES) { if (md >= m * 100 + d) cur = key; }
  return cur;
};

const SeasonalEdition = () => {
  const { t } = useLang();
  const sab = currentSabbat();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/store/products")).data });
  const item = products.find((p) => p.sabbat === sab && p.active !== false);
  if (!item) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6" data-testid="seasonal-edition">
      <Link to="/store" data-testid="seasonal-edition-link" className="group grid md:grid-cols-[220px_1fr] gap-6 items-center rounded-2xl border border-indigo-800/40 bg-gradient-to-br from-indigo-950/40 to-[#121418] p-5 hover:border-indigo-500/60 transition-all">
        <div className="w-full h-48 md:h-40 rounded-xl overflow-hidden"><EventImage src={item.image} alt={item.name} imgClassName="w-full h-full object-cover" /></div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-indigo-300 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> {t("seasonal_tag")} · {t(`sabbat_${sab}`)}</div>
          <h3 className="font-serif text-2xl sm:text-3xl text-amber-100 mb-2 group-hover:text-amber-50">{item.name}</h3>
          <p className="text-stone-400 text-sm line-clamp-2 mb-4 max-w-xl">{item.description}</p>
          <div className="flex items-center gap-4">
            <span className="font-serif text-xl text-amber-400">{formatPrice(item.price_cents, item.currency)}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-200 group-hover:gap-2.5 transition-all">{t("seasonal_cta")} <ArrowRight className="w-4 h-4" /></span>
          </div>
        </div>
      </Link>
    </section>
  );
};

// Bandeaux thématiques par type (photo de fond en lien avec le thème)
const TYPE_BANNERS = {
  markets: "https://images.unsplash.com/photo-1783231607347-8b7ab4525a17?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  festivals: "https://images.unsplash.com/photo-1569430251091-da3b108cff29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  rituals: "https://images.unsplash.com/photo-1651833757675-ac8580cf4ffd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  concerts: "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

// Section groupée par TYPE d'événement (bandeau photo thématique + grille)
const TypeSection = ({ title, icon: Icon, events, emptyLabel, viewAll, banner }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid={`home-type-${title}`}>
    <div className="relative overflow-hidden rounded-2xl mb-8 border border-amber-900/30 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.9)] group" data-testid={`home-type-banner-${title}`}>
      {banner && <img src={banner} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,12,14,0.94) 0%, rgba(11,12,14,0.72) 42%, rgba(11,12,14,0.32) 100%)" }} />
      <div className="absolute inset-0 noise-bg opacity-20" />
      <div className="relative flex items-center justify-between gap-4 px-6 sm:px-9 py-9 sm:py-12">
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-amber-50 text-glow flex items-center gap-3">
          {Icon && <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400 shrink-0" />} {title}
        </h2>
        <Link to="/events" className="font-mono text-xs uppercase tracking-wider text-amber-200 hover:text-amber-100 flex items-center gap-1.5 shrink-0 bg-[#0B0C0E]/60 backdrop-blur px-3.5 py-2 rounded-full border border-amber-800/40 hover:border-amber-500/60 transition-colors">{viewAll} <ArrowRight className="w-3.5 h-3.5" /></Link>
      </div>
    </div>
    {events.length > 0 ? (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}
      </div>
    ) : (
      <p className="text-stone-500 py-4 text-sm">{emptyLabel}</p>
    )}
  </section>
);

// Séparateur discret orné de la flamme du logo
const FlameDivider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4" aria-hidden>
    <div className="flex items-center justify-center gap-4 opacity-70">
      <span className="h-px w-16 sm:w-28 bg-gradient-to-r from-transparent to-amber-700/50" />
      <img src="/brand/swiss-pagan-flame.png" alt="" className="h-6 sm:h-7 w-auto object-contain flame-flicker" />
      <span className="h-px w-16 sm:w-28 bg-gradient-to-l from-transparent to-amber-700/50" />
    </div>
  </div>
);

export default function Home() {
  const { t, tCategory } = useLang();

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const homeJsonLd = [
    { "@context": "https://schema.org", "@type": "Organization", name: "swiss-pagan.ch", url: origin,
      logo: `${origin}/favicon.ico`, description: t("hero_subtitle") },
    { "@context": "https://schema.org", "@type": "WebSite", name: "swiss-pagan.ch", url: origin,
      potentialAction: { "@type": "SearchAction", target: `${origin}/events?q={search_term_string}`, "query-input": "required name=search_term_string" } },
  ];

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: mapEvents = [] } = useQuery({ queryKey: ["mapEventsHome"], queryFn: async () => (await api.get("/events", { params: { limit: 200 } })).data });
  const { data: organizers = [] } = useQuery({ queryKey: ["trendingOrganizers"], queryFn: async () => (await api.get("/organizers/trending", { params: { limit: 6 } })).data });
  const { data: partners = [] } = useQuery({ queryKey: ["partnersPublic"], queryFn: async () => (await api.get("/partners")).data });
  const founders = partners.filter((p) => p.tier === "founder");

  const weekend = React.useMemo(() => {
    const range = weekendRange();
    return mapEvents.filter((e) => isThisWeekend(e, range)).slice(0, 6);
  }, [mapEvents]);

  const byType = React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const up = mapEvents
      .filter((e) => (e.start_date || "").slice(0, 10) >= today)
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    return {
      markets: up.filter((e) => MARKET_CATS.includes(e.category)).slice(0, 3),
      festivals: up.filter((e) => FESTIVAL_CATS.includes(e.category)).slice(0, 3),
      rituals: up.filter((e) => RITUAL_CATS.includes(e.category)).slice(0, 3),
      concerts: up.filter((e) => e.category === "concert" || e.nordic).slice(0, 3),
    };
  }, [mapEvents]);

  const statItems = [
    { key: "events", label: t("stat_events") },
    { key: "cantons", label: t("stat_cantons") },
    { key: "organizers", label: t("stat_organizers") },
    { key: "categories", label: t("stat_categories") },
  ];

  return (
    <>
      <Seo title="" description={t("hero_subtitle")} image={HERO} canonicalPath="/" jsonLd={homeJsonLd} />

      {/* SABBAT À VENIR — tout en haut */}
      <SabbatBanner />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(217,119,6,0.18), rgba(11,12,14,0.75) 45%, rgba(11,12,14,0.98) 90%)" }} />
          <div className="absolute inset-0 noise-bg opacity-40" />
          {/* Braises païennes — halo + particules montantes (subtil, CSS only) */}
          <div className="ember-glow" aria-hidden />
          <div className="ember-layer" aria-hidden>
            <span className="ember" style={{ left: "12%", animationDuration: "9s", animationDelay: "0s", "--drift": "18px" }} />
            <span className="ember" style={{ left: "24%", animationDuration: "11s", animationDelay: "2.5s", "--drift": "-14px" }} />
            <span className="ember" style={{ left: "38%", animationDuration: "8s", animationDelay: "1.2s", "--drift": "10px" }} />
            <span className="ember" style={{ left: "52%", animationDuration: "12s", animationDelay: "3.5s", "--drift": "-20px" }} />
            <span className="ember" style={{ left: "68%", animationDuration: "10s", animationDelay: "0.8s", "--drift": "16px" }} />
            <span className="ember" style={{ left: "84%", animationDuration: "13s", animationDelay: "4.2s", "--drift": "-12px" }} />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-800/40 bg-amber-950/30 backdrop-blur mb-8">
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">{t("brand_tag")}</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-amber-100 text-glow leading-[0.95] mb-6">
              {t("hero_title")}
            </h1>
            <RotatingRitual />
            <p className="text-lg text-stone-300/90 leading-relaxed max-w-2xl mb-10">{t("hero_subtitle")}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/events" data-testid="hero-cta-events" className="group inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-7 py-3.5 rounded-md shadow-lg transition-all duration-300 hover:scale-[1.02] border border-amber-400/30">
                {t("hero_cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/map" data-testid="hero-cta-map" className="inline-flex items-center gap-2 bg-[#181A20]/80 backdrop-blur hover:bg-[#22252D] text-stone-200 font-medium px-7 py-3.5 rounded-md border border-[#272A34] hover:border-amber-500/40 transition-all">
                {t("hero_cta_map")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS — juste sous le Hero */}
      <section className="border-y border-[#272A34] bg-[#0B0C0E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#272A34]">
          {statItems.map((s) => (
            <div key={s.key} className="py-10 px-4 text-center" data-testid={`stat-${s.key}`}>
              <div className="font-serif text-4xl sm:text-5xl text-amber-400 text-glow">{stats?.[s.key] ?? "—"}</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CE WEEK-END */}
      <section data-testid="home-weekend" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 border-b border-amber-900/30 pb-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" /> {t("weekend_tag")}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{t("weekend_title")}</h2>
            <p className="text-stone-400 mt-2 text-sm sm:text-base">{t("weekend_subtitle")}</p>
          </div>
          <Link to="/events" className="font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 flex items-center gap-1.5 shrink-0">{t("view_all")} <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        {weekend.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {weekend.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}
          </div>
        ) : (
          <p className="text-stone-500 py-6" data-testid="home-weekend-none">{t("weekend_none")}</p>
        )}
      </section>

      {/* PRÈS DE CHEZ VOUS */}
      <NearYou events={mapEvents} />

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="mb-8">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">{t("section_map")}</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-stone-100">{t("section_map_desc")}</h2>
        </div>
        <EventMap events={mapEvents} height="480px" />
      </section>

      <FlameDivider />

      {/* PAR TYPE : MARCHÉS → FESTIVALS → CONCERTS */}
      <TypeSection title={t("home_type_markets")} icon={Store} events={byType.markets} emptyLabel={t("home_type_empty")} viewAll={t("view_all")} banner={TYPE_BANNERS.markets} />
      <TypeSection title={t("home_type_festivals")} icon={Tent} events={byType.festivals} emptyLabel={t("home_type_empty")} viewAll={t("view_all")} banner={TYPE_BANNERS.festivals} />
      <TypeSection title={t("home_type_rituals")} icon={Sparkles} events={byType.rituals} emptyLabel={t("home_type_empty")} viewAll={t("view_all")} banner={TYPE_BANNERS.rituals} />
      <TypeSection title={t("home_type_concerts")} icon={Music} events={byType.concerts} emptyLabel={t("home_type_empty")} viewAll={t("view_all")} banner={TYPE_BANNERS.concerts} />

      {/* CATEGORIES — explorer par univers (clôt la découverte des événements) */}
      <Section title={t("section_categories")} link="/categories" linkLabel={t("view_all")}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => {
            const meta = CATEGORY_META[c.key] || {};
            const Icon = meta.icon;
            return (
              <Link key={c.key} to={`/category/${c.key}`} data-testid={`home-category-${c.key}`}
                className="group relative overflow-hidden rounded-lg border border-[#272A34] hover:border-amber-500/50 bg-[#121418] hover:bg-[#181A20] p-6 transition-all duration-300 hover:-translate-y-1 fade-up"
                style={{ animationDelay: `${i * 50}ms` }}>
                {Icon && <Icon className="w-8 h-8 mb-4" style={{ color: meta.color }} />}
                <div className="font-serif text-base text-stone-100 group-hover:text-amber-100 leading-tight">{tCategory(c.key)}</div>
                <div className="mt-1 font-mono text-xs text-stone-500">{c.count} {t("results_count")}</div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* MOON & SABBAT — l'almanach païen fait la transition vers la communauté */}
      <MoonPhase />

      <FlameDivider />

      {/* LE CLAN — membre à la une + annuaire actif */}
      <ClanSpotlight />
      <ClanHighlight limit={6} />

      {/* TRENDING ORGANIZERS */}
      {organizers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="flex items-end justify-between gap-4 mb-8 border-b border-amber-900/30 pb-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">{t("trending_subtitle")}</div>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{t("trending_title")}</h2>
            </div>
            <Link to="/organisateurs" data-testid="home-all-organizers" className="font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 flex items-center gap-1.5 shrink-0">{t("view_all")} <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {organizers.map((o, i) => (
              <Link key={o.user_id} to={`/u/${o.user_id}`} data-testid={`trending-organizer-${o.user_id}`}
                className="group flex flex-col items-center text-center bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                {o.picture ? <img src={o.picture} alt={o.name} className="w-16 h-16 rounded-full object-cover border-2 border-amber-800/40 mb-3" /> : <span className="w-16 h-16 rounded-full bg-amber-900/40 flex items-center justify-center mb-3"><Flame className="w-7 h-7 text-amber-400" /></span>}
                <div className="font-serif text-sm text-stone-100 group-hover:text-amber-100 leading-tight line-clamp-2">{o.name}</div>
                <div className="mt-2 font-mono text-[11px] text-stone-500">{o.follower_count} {t("followers")} · {o.event_count} {t("events_count")}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ARTISANS & ARTISTES À DÉCOUVRIR */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8"><FeaturedPros placement="home" limit={3} /></div>

      {/* BOUTIQUE SAISONNIÈRE — commerce */}
      <SeasonalEdition />

      <FlameDivider />


      {/* PARTENAIRES FONDATEURS — mis en avant */}
      {founders.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-4 pt-16" data-testid="home-founders">
          <div className="relative overflow-hidden rounded-2xl border border-amber-700/40 bg-gradient-to-br from-amber-900/30 to-[#0B0C0E] p-6 sm:p-8">
            <div className="ember-glow" aria-hidden />
            <div className="relative">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-1 flex items-center gap-2">
                <img src="/brand/swiss-pagan-flame.png" alt="" className="h-4 w-auto flame-flicker" /> Partenaires fondateurs
              </div>
              <p className="text-sm text-stone-400 mb-6">Celles et ceux qui portent la flamme avec nous depuis le début.</p>
              <div className="flex flex-wrap items-stretch gap-4">
                {founders.map((p) => {
                  const inner = (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-800/40 bg-[#121418]/70 px-5 py-3.5 hover:border-amber-500/60 hover:-translate-y-0.5 transition-all">
                      {p.logo
                        ? <img src={p.logo} alt={p.name} className="h-9 object-contain" />
                        : <span className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-stone-950 font-serif flex items-center justify-center border border-[#121418] shrink-0">{(p.name || "?").charAt(0)}</span>}
                      <span className="font-serif text-lg text-stone-100">{p.name}</span>
                    </div>
                  );
                  return p.website
                    ? <a key={p.id} href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`home-founder-${p.id}`}>{inner}</a>
                    : <div key={p.id} data-testid={`home-founder-${p.id}`}>{inner}</div>;
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* NOS PARTENAIRES OFFICIELS — présentation chaleureuse */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-10 pt-10" data-testid="home-official-partners">
        <div className="relative overflow-hidden rounded-2xl border border-amber-800/30 bg-gradient-to-br from-amber-950/25 via-[#0E0F12] to-[#0B0C0E] p-6 sm:p-10">
          <div className="ember-glow" aria-hidden />
          <div className="relative">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2">
                  <img src="/brand/swiss-pagan-flame.png" alt="" className="h-4 w-auto flame-flicker" /> Le cercle Swiss Pagan
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-amber-50 text-glow">Nos partenaires &amp; complices</h2>
              </div>
              <Link to="/devenir-partenaire" className="font-mono text-xs uppercase tracking-wider text-amber-400 hover:text-amber-300 flex items-center gap-1.5 shrink-0">Rejoindre le cercle <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <p className="text-sm text-stone-400 max-w-3xl mb-9 leading-relaxed">
              Autour de la flamme, un réseau de créateurs et d'acteurs passionnés qui font vivre les traditions, la spiritualité et la culture païenne en Suisse et au-delà.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[
                { slug: "entre-les-mondes", name: "Entre Les Mondes", url: "https://www.entrelesmondes.net", cover: "https://images.unsplash.com/photo-1737995801710-3b4ee1d6b7e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", tag: "Tambours & traditions", desc: "Création de tambours traditionnels, expériences, formations et pratiques liées aux traditions européennes." },
                { slug: "swisseire", name: "SwissEire", url: "https://www.swisseire.com", cover: "https://images.unsplash.com/photo-1502758185688-8f322e06d0eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", tag: "Voyages en Irlande", desc: "Voyages immersifs en Irlande, à la découverte des paysages, des traditions et des sites anciens." },
                { slug: "lesprit-de-dagaz", name: "L'Esprit de Dagaz", url: "https://www.lespritdedagaz.ch", cover: "https://images.pexels.com/photos/6892700/pexels-photo-6892700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=900", tag: "Podcast & média", desc: "Podcast et média consacré aux traditions, à la spiritualité et aux cultures païennes." },
              ].map((p, i) => (
                <a
                  key={p.slug}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`home-partner-${p.slug}`}
                  className="group flex flex-col rounded-xl overflow-hidden border border-[#272A34] bg-[#121418] transition-all duration-300 hover:border-amber-500/60 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(217,119,6,0.45)] fade-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="relative h-36 overflow-hidden">
                    <img src={p.cover} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,12,14,0.15) 0%, rgba(11,12,14,0.55) 55%, rgba(18,20,24,0.98) 100%)" }} />
                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-200 bg-[#0B0C0E]/70 backdrop-blur px-2.5 py-1 rounded-full border border-amber-800/50">{p.tag}</span>
                  </div>
                  <div className="relative px-5 pb-5 -mt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-stone-950 font-serif text-lg flex items-center justify-center border-2 border-[#121418] shadow-lg shrink-0">{p.name.charAt(0)}</span>
                      <h3 className="font-serif text-lg text-stone-100 group-hover:text-amber-100 transition-colors leading-tight">{p.name}</h3>
                    </div>
                    <p className="text-xs text-stone-400 leading-relaxed mb-4">{p.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 group-hover:gap-2.5 transition-all">Visiter le site <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRO / B2B DISCREET */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-8" data-testid="home-pro-section">
        <div className="rounded-2xl border border-[#272A34] bg-gradient-to-br from-[#121418] to-[#0B0C0E] p-8 sm:p-10">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-6">Vous êtes professionnel ?</div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-[#272A34] bg-[#0B0C0E]/60 p-6">
              <h3 className="font-serif text-xl text-amber-100 mb-2">Je suis organisateur</h3>
              <p className="text-sm text-stone-400 mb-5">Publiez vos événements et gagnez en visibilité auprès d'une communauté passionnée.</p>
              <Link to="/submit" data-testid="home-pro-organizer" className="inline-flex items-center gap-2 bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 px-5 py-2.5 rounded-md text-sm transition-all">Annoncer un événement <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="rounded-xl border border-[#272A34] bg-[#0B0C0E]/60 p-6">
              <h3 className="font-serif text-xl text-amber-100 mb-2">Marque, festival ou institution</h3>
              <p className="text-sm text-stone-400 mb-5">Associez votre marque à l'univers Swiss Pagan et touchez une audience réellement ciblée.</p>
              <Link to="/devenir-partenaire" data-testid="home-pro-partner" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md text-sm border border-amber-400/30 transition-all">Devenir partenaire <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ORGANIZER CTA supprimé (redondant avec le bloc pro) */}
    </>
  );
}

const Section = ({ title, link, linkLabel, children }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
    <div className="flex items-end justify-between mb-8 border-b border-amber-900/30 pb-4">
      <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{title}</h2>
      {link && <Link to={link} className="font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 flex items-center gap-1.5 shrink-0">{linkLabel} <ArrowRight className="w-3.5 h-3.5" /></Link>}
    </div>
    {children}
  </section>
);
